import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import Spinner from "../../../../../Components/common/Spinner/Spinner";
import HeaderStatusToggle from "../../Components/HeaderStatusToggle";
import Backdrop from "../../../../../Components/UI/Backdrop/Backdrop";
import LlmConfigFields from "./LlmConfigFields";
import {
  INITIAL_LLM_CONFIGS,
  llmConfigsFromDB,
  providerChangeConfigs,
  validateLlmConfigs,
  buildLlmRequestBody,
} from "./llmConfig";

import {
  RequestGetLlmSettings,
  RequestSetLlmSettings,
  RequestTestLlmSettings,
  RequestToggleLlmSettings,
  ClearLlmTestResult,
} from "../../../../../store/actions/settingsActions";

import "./styles.scss";

const LlmSettings = () => {
  const dispatch = useDispatch();

  const llmSettings = useSelector((state) => state.settings.llmSettings);
  const getLoading = useSelector((state) => state.settings.llmSettingsLoading);
  const setLoading = useSelector((state) => state.settings.setLlmSettingsLoading);
  const testLoading = useSelector((state) => state.settings.llmTestLoading);
  const testResult = useSelector((state) => state.settings.llmTestResult);
  const toggleLoading = useSelector((state) => state.settings.toggleLlmLoading);

  const [configs, setConfigs] = useState(INITIAL_LLM_CONFIGS);
  const [errors, setErrors] = useState({});

  const savedConfigs = useMemo(() => llmConfigsFromDB(llmSettings), [llmSettings]);
  // The stored key is reused only while the provider stays the same
  const hasStoredKey = !!llmSettings?.has_api_key && llmSettings?.provider === configs.provider;

  useEffect(() => {
    dispatch(RequestGetLlmSettings());
    dispatch(ClearLlmTestResult());
  }, [dispatch]);

  useEffect(() => {
    setConfigs(savedConfigs);
  }, [savedConfigs]);

  const updateConfigs = (changes) => {
    setConfigs((prev) => ({ ...prev, ...changes }));
    if (testResult) dispatch(ClearLlmTestResult());
  };

  const providerChangeHandler = (item) => {
    setErrors({});
    updateConfigs(providerChangeConfigs(item.id, configs, savedConfigs));
  };

  const validateConfigs = (current, requireAll) => {
    const newErrors = validateLlmConfigs(current, { requireAll, hasStoredKey });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const isChanged = JSON.stringify(configs) !== JSON.stringify(savedConfigs);

  const saveHandler = () => {
    // Fields only have to be complete when the LLM is enabled
    if (!validateConfigs(configs, configs.active)) return;
    // On success the settings are refetched, which also clears the typed key
    dispatch(RequestSetLlmSettings(buildLlmRequestBody(configs)));
  };

  // Disabling is saved right away. Enabling only unlocks the form, the LLM goes live
  // after Save validates and tests the configuration.
  const toggleHandler = (active) => {
    if (active) {
      updateConfigs({ active: true });
      return;
    }
    setErrors({});
    if (savedConfigs.active) {
      dispatch(RequestToggleLlmSettings({ active: false }));
    } else {
      setConfigs(savedConfigs);
      if (testResult) dispatch(ClearLlmTestResult());
    }
  };

  const isPendingActivation = configs.active && !savedConfigs.active;

  const testHandler = () => {
    if (!validateConfigs(configs, true)) return;
    dispatch(RequestTestLlmSettings(buildLlmRequestBody(configs)));
  };

  return (
    <div className="llm-settings">
      <div className="llm-settings__header">
        <h1>LLM</h1>
      </div>

      <div className="llm-settings__body">
        <div className="llm-settings__body__header">
          <h3>LLM</h3>
          <HeaderStatusToggle
            active={configs.active}
            onClick={(active) => !toggleLoading && toggleHandler(active)}
          />
          <p>
            NOTE: When enabled, CyberFortress AI chat and AI Decision use the LLM configured below.
            When disabled, the built-in CyberFortress AI is used.
          </p>
        </div>

        <div className="llm-settings__body__content">
          {getLoading && !llmSettings?.provider ? (
            <Spinner width={20} height={20} />
          ) : (
            <>
              <LlmConfigFields
                configs={configs}
                onChange={updateConfigs}
                onProviderChange={providerChangeHandler}
                hasStoredKey={hasStoredKey}
              />

              <div className="llm-settings__actions">
                <button
                  className="m-r-15 default-button"
                  onClick={testHandler}
                  disabled={testLoading || setLoading}>
                  {testLoading ? <Spinner width={12} height={12} /> : "Test Connection"}
                </button>
                <button
                  className="primary-button"
                  onClick={saveHandler}
                  disabled={setLoading || !isChanged}>
                  {setLoading ? <Spinner width={12} height={12} /> : "Save"}
                </button>
              </div>

              {isPendingActivation && (
                <p className="llm-settings__hint">
                  Not saved yet. Click Save to activate the LLM. The connection is tested before the
                  settings are saved.
                </p>
              )}

              {testResult && (
                <p
                  className={`llm-settings__test-result ${
                    testResult.success ? "llm-settings__test-result--success" : ""
                  }`}>
                  {testResult.message}
                  {testResult.latency_ms !== undefined && ` (${testResult.latency_ms} ms)`}
                </p>
              )}

              {Object.keys(errors).length > 0 && (
                <div>
                  {Object.keys(errors).map((key) => (
                    <p key={key} className="llm-settings__error">
                      {errors[key]}
                    </p>
                  ))}
                </div>
              )}
            </>
          )}
          <Backdrop
            spinner={false}
            absolute
            show={!configs?.active}
            message="LLM settings are inactive! Turn on the Status toggle above to activate them."
            messageStyle={{ paddingTop: 0 }}
          />
        </div>
      </div>
    </div>
  );
};

export default LlmSettings;
